import { Column } from "@/components/common/Table";
import { MedicationFillHistory } from "./medicationFillHistory";
import { useRef, useEffect, useState } from "react";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";

// Component to handle conditional tooltip for any cell
const TruncatedCell: React.FC<{
  content: string;
  className?: string;
}> = ({ content, className = "" }) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (!textRef.current) return;
      setIsTruncated(
        textRef.current.scrollWidth > textRef.current.clientWidth
      );
    };

    const id = setTimeout(checkTruncation, 0);
    window.addEventListener("resize", checkTruncation);

    return () => {
      clearTimeout(id);
      window.removeEventListener("resize", checkTruncation);
    };
  }, [content]);

  const cell = (
    <div
      ref={textRef}
      className={`
        truncate whitespace-nowrap overflow-hidden
        w-full max-w-full
        ${className}
        ${isTruncated ? "cursor-pointer" : ""}
      `}
    >
      {content}
    </div>
  );

  if (!isTruncated) return cell;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{cell}</TooltipTrigger>
        <TooltipContent
          side="top"
          align="start"
          className="max-w-xs bg-gray-700 text-grey-100 break-words z-50"
        >
          <Typography variant="regular-12" className="text-gray-100">
            {content}
          </Typography>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const columns: Column<MedicationFillHistory>[] = [
  {
    key: "drug",
    label: "Drug",
    isSortable: false,
    cellClassName: "w-[220px] max-w-[220px]",
    headerClassName: "w-[220px]",
    render: (row) => (
      <TruncatedCell
        content={row.drug || "-"}
        className="text-grey-800 dark:text-white"
      />
    ),
  },
  {
    key: "fillDate",
    label: "Fill Date",
    isSortable: false,
    cellClassName: "w-[100px] max-w-[100px]",
    headerClassName: "w-[100px]",
    render: (row) => (
      <TruncatedCell
        content={row.fillDate || "-"}
        className="text-grey-800 dark:text-white"
      />
    ),
  },
  {
    key: "daysSupply",
    label: "Days Supply",
    isSortable: false,
    cellClassName: "w-[106px] max-w-[106px]",
    headerClassName: "w-[106px]",
    render: (row) => (
      <TruncatedCell
        content={
          row.daysSupply !== null && row.daysSupply !== undefined
            ? String(row.daysSupply)
            : "-"
        }
        className="text-grey-800 dark:text-white"
      />
    ),
  },
  {
    key: "prescriber",
    label: "Prescriber",
    isSortable: false,
    cellClassName: "w-[130px] max-w-[130px]",
    headerClassName: "w-[130px]",
    render: (row) => (
      <TruncatedCell
        content={row.prescriber || "-"}
        className="text-grey-800 dark:text-white"
      />
    ),
  },
  {
    key: "pharmacy",
    label: "Pharmacy",
    isSortable: false,
    cellClassName: "w-[140px] max-w-[140px]",
    headerClassName: "w-[140px]",
    render: (row) => (
      <TruncatedCell
        content={row.pharmacy || "-"}
        className="text-grey-800 dark:text-white"
      />
    ),
  },
  {
    key: "drugType",
    label: "Drug Type",
    isSortable: false,
    cellClassName: "w-[100px] max-w-[100px]",
    headerClassName: "w-[100px]",
    render: (row) => (
      <TruncatedCell
        content={row.drugType || "-"}
        className="text-grey-800 dark:text-white"
      />
    ),
  },
  {
    key: "drugStrength",
    label: "Drug Strength",
    isSortable: false,
    cellClassName: "w-[120px] max-w-[120px]",
    headerClassName: "w-[120px]",
    render: (row) => (
      <TruncatedCell
        content={row.drugStrength || "-"}
        className="text-grey-800 dark:text-white"
      />
    ),
  },
  {
    key: "class",
    label: "Class",
    isSortable: false,
    cellClassName: "w-[140px] max-w-[140px]",
    headerClassName: "w-[140px]",
    render: (row) => (
      <TruncatedCell
        content={row.class || "-"}
        className="text-grey-800 dark:text-white"
      />
    ),
  },
];
